import { useSyndeticsConfig } from "@tlc/ls2pac/components/SyndeticsConfig";
import React, { useEffect, useState } from "react";
import { ResourceDWR } from "../../../../dto/ResourceDWR";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  getISBNList,
  getUPCList,
} from "@tlc/ls2pac/app/utils/PACStandardNumberUtil";
import {
  LibraryThingEnrichment,
  LibraryThingMetaData,
  SyndeticsUnboundUtil,
} from "@tlc/syndetics-util";
import { UnboundClasses } from "./SyndeticsRelatedContent";
import { TransitionGroup } from "react-transition-group";
import { useLanguage } from "@tlc/ls2pac/components/Language";

interface EnrichmentSectionProps {
  enrichment: LibraryThingEnrichment;
  enrichmentsShown: LibraryThingEnrichment[] | undefined;
}

export const EnrichmentSection = ({
  enrichment,
  enrichmentsShown,
}: EnrichmentSectionProps) => {
  const loading = enrichmentsShown === undefined;
  const isShown = enrichmentsShown?.includes(enrichment) ?? false;
  const shouldRender = loading || isShown;
  const [expanded, setExpanded] = useState<boolean>(isShown);

  useEffect(() => setExpanded(isShown), [isShown]);

  if (!shouldRender) {
    return null;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={!loading && isShown ? (_, e) => setExpanded(e) : undefined}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {loading && <Skeleton width={200} />}
        {!loading && isShown && (
          <Typography>
            {SyndeticsUnboundUtil.EnrichmentLabel.get(enrichment)}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails className={UnboundClasses[enrichment]} />
    </Accordion>
  );
};

export interface SyndeticsMoreInfoContentProps {
  resource: ResourceDWR;
}
export const SyndeticsMoreInfoContent = ({
  resource,
}: SyndeticsMoreInfoContentProps) => {
  const {
    hasMoreInfoTabEnrichments,
    enrichmentsEnabled,
    moreInfoTabEnrichments,
    util,
    syndeticsConfig,
  } = useSyndeticsConfig();
  const { translate } = useLanguage();
  const id = `resource_${resource?.id}_related`;
  const [enrichmentsShown, setEnrichmentsShown] =
    useState<LibraryThingEnrichment[]>(undefined);
  const [noContent, setNoContent] = useState<boolean>();

  useEffect(() => {
    const refreshMetaData = async () => {
      var isbn = getISBNList(resource.standardNumbers);
      var upc = getUPCList(resource.standardNumbers);
      const metaData: LibraryThingMetaData = {
        unbound_containing_selector: `#${id}`,
        title: resource.shortTitle,
        author: resource.shortAuthor,
        isbn: isbn,
        upc: upc,
        usemegadiv: false,
        branchFilters: syndeticsConfig.branchFilters,
      };
      try {
        const { errors, enrichmentsShown } = await util.refreshMetaData(
          metaData
        );
        setEnrichmentsShown(enrichmentsShown);
        const anyLoaded = moreInfoTabEnrichments.some((enrichment) =>
          enrichmentsShown.includes(enrichment)
        );
        setNoContent(!anyLoaded);

        console.log("Responded", enrichmentsShown, errors);
      } catch (e: unknown) {
        console.error(e);
        setNoContent(true);
      }
    };
    if (
      util &&
      syndeticsConfig?.active &&
      hasMoreInfoTabEnrichments &&
      resource?.id
    ) {
      refreshMetaData();
    }
  }, [util, syndeticsConfig?.active, hasMoreInfoTabEnrichments, resource?.id]);

  if (!hasMoreInfoTabEnrichments) {
    return null;
  }

  return (
    <Box id={id}>
      <TransitionGroup>
        {moreInfoTabEnrichments.map((enrichment) => {
          if (enrichmentsEnabled.includes(enrichment)) {
            return (
              <EnrichmentSection
                key={enrichment}
                enrichment={enrichment}
                enrichmentsShown={enrichmentsShown}
              />
            );
          } else {
            return null;
          }
        })}
        {noContent && <p>{translate("noContent")}</p>}
      </TransitionGroup>
    </Box>
  );
};
